"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from "recharts";
import { CircleHelp, Download, ChevronDown, TrendingUp, AlertTriangle, Loader2 } from "lucide-react";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import axiosInstance from "@/api/axios";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";

// ── Horizon options ────────────────────────────────────────────
const HORIZON_OPTIONS = [
  { label: "1 Week",   value: "1w",  periods: 7,   unit: "days"   },
  { label: "2 Weeks",  value: "2w",  periods: 14,  unit: "days"   },
  { label: "1 Month",  value: "1m",  periods: 4,   unit: "weeks"  },
  { label: "3 Months", value: "3m",  periods: 12,  unit: "weeks"  },
  { label: "6 Months", value: "6m",  periods: 26,  unit: "weeks"  },
  { label: "1 Year",   value: "1y",  periods: 12,  unit: "months" },
];

// ── Custom tooltip ─────────────────────────────────────────────
const ForecastTooltip = ({ active, payload, label, selfAdjust }) => {
  if (!active || !payload || !payload.length) return null;
  const unit = selfAdjust ? "kg" : "g";

  return (
    <div className="rounded-lg border bg-background p-3 shadow-md">
      <p className="font-medium mb-2">{label}</p>
      {payload.map((p) => {
        if (p.name === "confidenceLow" || p.name === "confidenceHigh" || p.name === "confidence") return null;
        const displayName = p.name === "actual" ? "Historical" : p.name === "forecast" ? "Forecast" : p.name;
        return (
          <div key={p.name} className="flex justify-between gap-4 text-sm">
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
              <span className="capitalize">{displayName}:</span>
            </div>
            <span className="font-medium">
              {typeof p.value === "number" ? p.value.toFixed(2) : "—"} {unit}
            </span>
          </div>
        );
      })}
      {payload.find((p) => p.name === "confidenceLow") && (
        <div className="flex justify-between gap-4 text-sm mt-2 pt-2 border-t">
          <span className="text-muted-foreground">95% CI:</span>
          <span className="font-mono text-xs">
            {payload.find((p) => p.name === "confidenceLow")?.value?.toFixed(2)} –{" "}
            {payload.find((p) => p.name === "confidenceHigh")?.value?.toFixed(2)} {unit}
          </span>
        </div>
      )}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────
export function WasteForecast({
  className,
  title,
  bins = [],
  timeRange = "Year",
  customDateRange = [{ startDate: null, endDate: null, key: "selection" }],
  isCustomDateRange = false,
  ...props
}) {
  const { t } = useTranslation();
  const [horizon, setHorizon] = useState("1m");
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selfAdjust, setSelfAdjust] = useState(false);
  const [modelInfo, setModelInfo] = useState(null);
  const exportRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const selectedHorizon = HORIZON_OPTIONS.find((h) => h.value === horizon);

  // ── Fetch forecast ─────────────────────────────────────────
  const fetchForecast = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        bins: bins.join(","),
        horizon,
      };

      if (
        isCustomDateRange &&
        customDateRange[0]?.startDate &&
        customDateRange[0]?.endDate
      ) {
        const s = customDateRange[0].startDate;
        const e = customDateRange[0].endDate;
        params.startDate =
          s instanceof Date ? s.toISOString().slice(0, 10) : new Date(s).toISOString().slice(0, 10);
        params.endDate =
          e instanceof Date ? e.toISOString().slice(0, 10) : new Date(e).toISOString().slice(0, 10);
      } else {
        params.timeRange = timeRange;
      }

      const response = await axiosInstance.get("/waste_forecast", { params });

      if (response.data) {
        const data = response.data.chart_data || [];

        // Bridge fix: make forecast line start from last actual point
        const firstForecastIdx = data.findIndex(
          (d) => d.forecast !== null && d.forecast !== undefined
        );
        if (firstForecastIdx > 0) {
          const lastActualIdx = firstForecastIdx - 1;
          data[lastActualIdx] = {
            ...data[lastActualIdx],
            forecast: data[lastActualIdx].actual,
          };
        }

        setChartData(data);
        setSelfAdjust(response.data.self_adjust || false);
        setModelInfo(response.data.model_info || null);
      }
    } catch (err) {
      console.error("Forecast fetch error:", err);
      setError(
        err.response?.data?.error ||
          "Failed to generate forecast. Ensure sufficient historical data exists."
      );
    } finally {
      setLoading(false);
    }
  }, [bins, horizon, timeRange, customDateRange, isCustomDateRange]);

  useEffect(() => {
    if (bins.length > 0) fetchForecast();
  }, [fetchForecast]);

  // ── Export CSV ─────────────────────────────────────────────
  const handleExportCSV = () => {
    if (!chartData.length) return;
    const unit = selfAdjust ? "kg" : "g";
    const headers = ["Period", `Actual (${unit})`, `Forecast (${unit})`, `CI Low (${unit})`, `CI High (${unit})`];
    const rows = chartData.map((d) =>
      [
        d.period,
        d.actual ?? "",
        d.forecast ?? "",
        d.confidenceLow ?? "",
        d.confidenceHigh ?? "",
      ].join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `waste_forecast_${horizon}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // ── Export PNG ─────────────────────────────────────────────
  const handleExportPNG = async () => {
    if (!exportRef.current) return;

    // Hide controls temporarily for cleaner screenshot
    const controls = exportRef.current.querySelector('.export-controls');
    if (controls) {
      controls.style.display = 'none';
    }

    try {
      const canvas = await html2canvas(exportRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      
      const link = document.createElement('a');
      link.download = `waste_forecast_${horizon}_${new Date().toISOString().slice(0, 10)}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('PNG export failed:', err);
    } finally {
      // Restore controls
      if (controls) {
        controls.style.display = '';
      }
      setExporting(false);
    }
  };

  // ── Split index for reference line ─────────────────────────
  const splitIndex = chartData.findIndex(
    (d) => d.forecast !== null && d.forecast !== undefined
  );
  const unit = selfAdjust ? "kg" : "g";

  return (
    <div
      ref={exportRef}
      className={cn(
        "lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4 exportable-section",
        className
      )}
      {...props}
    >
      <div className="relative">
        <div className="flex justify-between items-center pb-1">
          <div>
            <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
              {title || "Waste Generation Forecast"}
            </h4>
            {modelInfo && (
              <p className="text-xs text-muted-foreground mt-0.5">
                SARIMA model · {selectedHorizon?.label} ahead
                <span className="ml-2 text-[10px] bg-muted px-1.5 py-0.5 rounded-full">
                  order ({modelInfo.order?.join(",")})
                  {modelInfo.seasonal_order && ` × (${modelInfo.seasonal_order.join(",")})`}
                </span>
              </p>
            )}
            {isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate && (
              <p className="text-sm text-muted-foreground mt-1">
                {new Date(customDateRange[0].startDate).toLocaleDateString()} - {new Date(customDateRange[0].endDate).toLocaleDateString()}
              </p>
            )}
          </div>

          <div className="flex items-center space-x-1 export-controls">
            {/* Horizon dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-sm font-medium min-w-[110px] justify-between"
                  disabled={loading}
                >
                  {selectedHorizon?.label ?? "Select"}
                  <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[130px]">
                {HORIZON_OPTIONS.map((opt) => (
                  <DropdownMenuItem
                    key={opt.value}
                    onClick={() => setHorizon(opt.value)}
                    className={cn(
                      "flex items-center justify-between cursor-pointer",
                      horizon === opt.value && "bg-muted font-semibold"
                    )}
                  >
                    {opt.label}
                    {horizon === opt.value && (
                      <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Export dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-primary"
                  disabled={!chartData.length || loading || exporting}
                >
                  <Download className="h-4 w-4 mr-1" />
                  {exporting ? "Exporting..." : "Export"}
                  <ChevronDown className="h-4 w-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportCSV}>
                  Download as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportPNG}>
                  Download as PNG
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <TooltipProvider>
              <UITooltip>
                <TooltipTrigger asChild>
                  <CircleHelp className="h-5 w-5 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent side="left">
                  <p>
                    Uses a SARIMA model trained on your historical waste data to
                    forecast future generation. Shaded area shows the 95%
                    confidence interval.
                  </p>
                </TooltipContent>
              </UITooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>

      <div className="px-1 pt-2 sm:px-2 sm:pt-2" style={{ width: '100%', height: 'calc(100% - 60px)' }}>
        {loading ? (
          <div className="flex items-center justify-center h-full w-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center px-8">
            <AlertTriangle className="h-8 w-8 text-amber-500" />
            <p className="text-sm font-medium text-foreground">Forecast unavailable</p>
            <p className="text-xs text-muted-foreground">{error}</p>
            <Button variant="outline" size="sm" onClick={fetchForecast} className="mt-2">
              Retry
            </Button>
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="text-muted-foreground text-center">
              <p className="mb-2 text-lg">No data available</p>
              <p className="text-sm">Select bins and time range to generate forecast</p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <defs>
                <linearGradient id="confidenceGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.02} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="hsl(var(--muted-foreground)/0.15)"
              />
              
              <XAxis
                dataKey="period"
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              
              <YAxis
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${v.toFixed(0)} ${unit}`}
              />
              
              <Tooltip
                content={<ForecastTooltip selfAdjust={selfAdjust} />}
                cursor={{ stroke: "hsl(var(--muted-foreground)/0.2)", strokeWidth: 1 }}
              />

              {/* Divider line between historical and forecast */}
              {splitIndex > 0 && chartData[splitIndex] && chartData[splitIndex].period && (
                <ReferenceLine
                  x={chartData[splitIndex].period}
                  stroke="hsl(var(--muted-foreground)/0.4)"
                  strokeDasharray="4 4"
                  label={{
                    value: "Today",
                    position: "insideTopLeft",
                    fontSize: 10,
                    fill: "hsl(var(--muted-foreground))",
                  }}
                />
              )}

              {/* Confidence interval band */}
              <Area
                type="monotone"
                dataKey="confidenceHigh"
                stroke="none"
                fill="url(#confidenceGrad)"
                legendType="none"
                name="confidence"
                connectNulls
              />
              <Area
                type="monotone"
                dataKey="confidenceLow"
                stroke="none"
                fill="transparent"
                legendType="none"
                name="confidence"
                connectNulls
              />

              {/* Historical line */}
              <Line
                type="monotone"
                dataKey="actual"
                stroke="hsl(var(--chart-6))"
                strokeWidth={2.5}
                dot={false}
                activeDot={{ r: 5 }}
                name="actual"
                connectNulls
              />

              {/* Forecast line */}
              <Line
                type="monotone"
                dataKey="forecast"
                stroke="#3b82f6"
                strokeWidth={2.5}
                strokeDasharray="6 4"
                dot={false}
                activeDot={{ r: 5 }}
                name="forecast"
                connectNulls
              />

              <Legend
                formatter={(value) => {
                  if (value === "actual") return "Historical";
                  if (value === "forecast") return "Forecast";
                  return value;
                }}
                wrapperStyle={{ fontSize: 12 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}